/**
 * @fileoverview Helper para generar archivos Excel de Inventario de Materiales
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';

/**
 * Calcula el total desde los movimientos (suma ingresos, resta egresos)
 * @param {Array} movimientos - Array de movimientos con cantidad e ingreso
 * @returns {number} Total calculado
 */
export function calcularTotalDesdeMovimientos(movimientos) {
  if (!movimientos || movimientos.length === 0) {
    return 0;
  }

  return movimientos.reduce((total, movimiento) => {
    const cantidad = movimiento.cantidad || 0;
    const esIngreso = movimiento.ingreso === true;
    
    return esIngreso ? total + cantidad : total - cantidad;
  }, 0);
}

/**
 * Crea la pestaña "Inventario Total" con todos los materiales
 * @param {Array} materiales - Array de materiales con expand
 * @returns {Object} Worksheet de Excel
 */
export function crearPestañaInventarioTotal(materiales) {
  const headers = ['Nombre', 'Total', 'Unidad'];
  
  const rows = materiales.map(material => [
    material.nombre || 'N/A',
    material.total || 0,
    material.expand?.unidad?.nombre || 'Sin unidad'
  ]);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  applyStylesInventarioTotal(ws, rows.length + 1);
  
  return ws;
}

/**
 * Crea una pestaña por material con sus movimientos
 * @param {Object} material - Material con expand de log_movimientos
 * @returns {Object} Worksheet de Excel
 */
export function crearPestañaPorMaterial(material) {
  const headers = ['Fecha', 'Usuario', 'Cantidad', 'Tipo'];
  
  const movimientos = material.expand?.log_movimientos || [];
  
  // Ordenar movimientos por fecha descendente (más reciente primero)
  const movimientosOrdenados = [...movimientos].sort((a, b) => {
    const dateA = new Date(a.created || 0).getTime();
    const dateB = new Date(b.created || 0).getTime();
    return dateB - dateA;
  });

  const rows = movimientosOrdenados.map(movimiento => {
    const fecha = movimiento.created 
      ? new Date(movimiento.created).toLocaleString('es-ES')
      : '';
    
    const usuario = movimiento.expand?.usuario?.name 
      || movimiento.expand?.usuario?.email 
      || 'N/A';
    
    const cantidad = movimiento.cantidad || 0;
    const tipo = movimiento.ingreso === true ? 'Ingreso' : 'Egreso';
    
    return [fecha, usuario, cantidad, tipo];
  });

  // Agregar fila de total al final
  const total = material.total || 0;
  rows.push(['', 'Total', total, '']);

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  applyStylesPorMaterial(ws, rows.length + 1);
  
  return ws;
}

/**
 * Genera un archivo Excel con pestaña "Inventario Total" y pestañas por material
 * @param {Array} materiales - Array de materiales con expand
 * @returns {void}
 */
export function generateExcel(materiales) {
  try {
    if (!materiales || materiales.length === 0) {
      throw new Error('No hay materiales para exportar');
    }

    const wb = XLSX.utils.book_new();

    // Crear pestaña "Inventario Total"
    const wsInventarioTotal = crearPestañaInventarioTotal(materiales);
    XLSX.utils.book_append_sheet(wb, wsInventarioTotal, 'Inventario Total');

    // Crear pestaña por cada material
    materiales.forEach(material => {
      const wsMaterial = crearPestañaPorMaterial(material);
      
      // Limitar nombre de pestaña a 31 caracteres (límite de Excel)
      const sheetName = material.nombre && material.nombre.length > 31 
        ? material.nombre.substring(0, 31) 
        : material.nombre || 'Sin nombre';
      
      XLSX.utils.book_append_sheet(wb, wsMaterial, sheetName);
      
      // Verificar que el total calculado coincide con el total del material
      const movimientos = material.expand?.log_movimientos || [];
      const totalCalculado = calcularTotalDesdeMovimientos(movimientos);
      const totalMaterial = material.total || 0;
      
      if (Math.abs(totalCalculado - totalMaterial) > 0.01) {
        console.warn(`Advertencia: El total calculado (${totalCalculado}) no coincide con el total del material (${totalMaterial}) para ${material.nombre}`);
      }
    });

    // Generar archivo y descargar
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = `Inventario_Materiales_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
}

/**
 * Aplica estilos a la pestaña "Inventario Total"
 * @param {Object} ws - Hoja de trabajo
 * @param {number} totalRows - Número total de filas
 */
function applyStylesInventarioTotal(ws, totalRows) {
  if (!ws || !ws['!ref']) {
    return;
  }

  const range = XLSX.utils.decode_range(ws['!ref']);

  // Estilos de headers
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellRef]) continue;

    ws[cellRef].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'D3D3D3' } },
      alignment: { wrapText: true, vertical: 'center' }
    };
  }

  // Estilos para todas las celdas de datos
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellRef]) continue;

      if (!ws[cellRef].s) {
        ws[cellRef].s = {
          alignment: { wrapText: true, vertical: 'top' }
        };
      }
    }
  }

  // Ajustar anchos de columna
  ws['!cols'] = [
    { wch: 30 }, // Nombre
    { wch: 15 }, // Total
    { wch: 20 }  // Unidad
  ];

  // Ajustar altura de filas
  ws['!rows'] = [];
  for (let row = 0; row <= range.e.r; row++) {
    ws['!rows'][row] = { hpt: 20 };
  }
}

/**
 * Aplica estilos a la pestaña por material
 * @param {Object} ws - Hoja de trabajo
 * @param {number} totalRows - Número total de filas
 */
function applyStylesPorMaterial(ws, totalRows) {
  if (!ws || !ws['!ref']) {
    return;
  }

  const range = XLSX.utils.decode_range(ws['!ref']);

  // Estilos de headers
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
    if (!ws[cellRef]) continue;

    ws[cellRef].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: 'D3D3D3' } },
      alignment: { wrapText: true, vertical: 'center' }
    };
  }

  // Estilos para todas las celdas de datos
  for (let row = range.s.r; row <= range.e.r; row++) {
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
      if (!ws[cellRef]) continue;

      if (!ws[cellRef].s) {
        ws[cellRef].s = {
          alignment: { wrapText: true, vertical: 'top' }
        };
      }

      // Fila de total (última fila) - fondo amarillo en columna de cantidad (columna C, índice 2)
      if (row === range.e.r && col === 2) {
        ws[cellRef].s.fill = { fgColor: { rgb: 'FFFF00' } };
        ws[cellRef].s.font = { bold: true };
      }
    }
  }

  // Ajustar anchos de columna
  ws['!cols'] = [
    { wch: 20 }, // Fecha
    { wch: 25 }, // Usuario
    { wch: 15 }, // Cantidad
    { wch: 15 }  // Tipo
  ];

  // Ajustar altura de filas
  ws['!rows'] = [];
  for (let row = 0; row <= range.e.r; row++) {
    ws['!rows'][row] = { hpt: 20 };
  }
}

